import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box, Container, Card, CardContent, Typography, Button,
  CircularProgress, Chip, Divider,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { getProductRequestById, updateProductRequest } from '../services/db';
import { useCart } from '../context/CartContext';

export default function AcceptQuote() {
  const [params] = useSearchParams();
  const navigate  = useNavigate();
  const { addToCart, updateCartItem } = useCart();

  const docId = params.get('docId');
  const token = params.get('token');

  const [state, setState] = useState('loading'); // loading | valid | invalid | accepted | error
  const [request, setRequest] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (!docId || !token) { setState('invalid'); return; }
    getProductRequestById(docId).then((req) => {
      if (!req) { setState('invalid'); return; }
      if (req.quoteToken !== token) { setState('invalid'); return; }
      if (req.status === 'Accepted') { setState('accepted'); setRequest(req); return; }
      if (req.status !== 'Quoted') { setState('invalid'); return; }
      setRequest(req);
      setState('valid');
    }).catch(() => setState('error'));
  }, [docId, token]);

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await updateProductRequest(docId, { status: 'Accepted', acceptedAt: new Date().toISOString() });

      const productName = [request.brand, request.model].filter(Boolean).join(' ') || request.category;
      const cartItemId  = request.id || request.refId;

      updateCartItem(cartItemId, { price: request.quotedPrice, isRequest: false, name: productName });

      if (!useCart) {
        addToCart({ id: cartItemId, name: productName, price: request.quotedPrice, image: '/images/products/request-placeholder.jpg' });
      }

      navigate('/cart');
    } catch {
      setState('error');
    } finally {
      setAccepting(false);
    }
  };

  const cardSx = {
    background: 'linear-gradient(135deg, rgba(255,107,107,0.08), rgba(255,217,61,0.12))',
    border: '1px solid rgba(255,107,107,0.2)',
    borderRadius: '16px',
  };

  if (state === 'loading') {
    return (
      <Box sx={{ minHeight: '70vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress sx={{ color: '#ff6b6b' }} />
      </Box>
    );
  }

  if (state === 'invalid' || state === 'error') {
    return (
      <Box sx={{ bgcolor: 'background.default', py: 10, minHeight: '70vh', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm">
          <Card sx={cardSx}>
            <CardContent sx={{ textAlign: 'center', py: 5 }}>
              <ErrorOutlineIcon sx={{ fontSize: 70, color: '#c0392b', mb: 2 }} />
              <Typography variant="h5" gutterBottom fontWeight={700}>Quote Link Invalid</Typography>
              <Typography color="text.secondary" sx={{ mb: 4, lineHeight: 1.8 }}>
                This quote link is invalid, expired, or has already been used.<br />
                Please contact us if you need help.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/')}
                sx={{ background: 'linear-gradient(135deg, #ff6b6b, #ffd93d)', color: '#1a1f21', fontWeight: 700 }}>
                Go to Homepage
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  if (state === 'accepted') {
    return (
      <Box sx={{ bgcolor: 'background.default', py: 10, minHeight: '70vh', display: 'flex', alignItems: 'center' }}>
        <Container maxWidth="sm">
          <Card sx={cardSx}>
            <CardContent sx={{ textAlign: 'center', py: 5 }}>
              <CheckCircleIcon sx={{ fontSize: 70, color: '#27ae60', mb: 2 }} />
              <Typography variant="h5" gutterBottom fontWeight={700}>Quote Already Accepted</Typography>
              <Typography color="text.secondary" sx={{ mb: 4 }}>
                You have already accepted this quote. Check your cart to complete the payment.
              </Typography>
              <Button variant="contained" onClick={() => navigate('/cart')}
                startIcon={<ShoppingCartIcon />}
                sx={{ background: 'linear-gradient(135deg, #ff6b6b, #ffd93d)', color: '#1a1f21', fontWeight: 700 }}>
                Go to Cart
              </Button>
            </CardContent>
          </Card>
        </Container>
      </Box>
    );
  }

  const productName = [request.brand, request.model].filter(Boolean).join(' ') || request.category;

  return (
    <Box sx={{ bgcolor: 'background.default', py: 8, minHeight: '70vh' }}>
      <Container maxWidth="sm">
        <Typography variant="h4" align="center" className="gradient-text" gutterBottom fontWeight={700} sx={{ mb: 1 }}>
          Your Quote is Ready
        </Typography>
        <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
          Review the details below and accept to proceed to payment.
        </Typography>

        <Card sx={cardSx}>
          <CardContent sx={{ p: 4 }}>
            {/* Quote badge */}
            <Box sx={{ textAlign: 'center', mb: 3 }}>
              <Chip
                icon={<LocalOfferIcon />}
                label={`Ref: ${request.id || request.refId}`}
                sx={{ bgcolor: 'rgba(41,128,185,0.15)', color: '#2980b9', border: '1px solid rgba(41,128,185,0.3)', fontFamily: 'monospace', fontWeight: 700 }}
              />
            </Box>

            {/* Product details */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Product Requested</Typography>
              <Typography variant="h6" fontWeight={700}>{productName}</Typography>
              <Typography variant="body2" color="text.secondary">Category: {request.category}</Typography>
              {request.quantity > 1 && <Typography variant="body2" color="text.secondary">Quantity: {request.quantity}</Typography>}
              {request.notes && <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>"{request.notes}"</Typography>}
            </Box>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', mb: 3 }} />

            {/* Price */}
            <Box sx={{ textAlign: 'center', mb: 3, p: 3, bgcolor: 'rgba(39,174,96,0.08)', borderRadius: 2, border: '1px solid rgba(39,174,96,0.25)' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Your Quoted Price</Typography>
              <Typography variant="h3" sx={{ color: '#27ae60', fontWeight: 800 }}>
                ${Number(request.quotedPrice).toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Quoted on {request.quotedAt ? new Date(request.quotedAt).toLocaleDateString() : '—'}
              </Typography>
            </Box>

            {/* CTA */}
            <Button
              fullWidth variant="contained" size="large"
              onClick={handleAccept}
              disabled={accepting}
              startIcon={accepting ? null : <ShoppingCartIcon />}
              sx={{
                py: 1.8, fontWeight: 800, fontSize: '1rem', borderRadius: '10px',
                background: 'linear-gradient(135deg, #ff6b6b, #ffd93d)',
                color: '#1a1f21',
                '&:hover': { opacity: 0.92 },
              }}
            >
              {accepting ? <CircularProgress size={24} sx={{ color: '#1a1f21' }} /> : 'Accept Quote & Pay'}
            </Button>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
              By accepting you agree to the quoted price. You will be taken to the cart to complete secure payment.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
